import React, { useRef, useState, useEffect, useContext, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { 
    Container,
    FilterMainText,
    FilterSelectDiv,
    FilterSelectInput,
    FilterSelectImg,
    FilterTextInput,
    ApplyFilterButton,
    WarningDiv,
    WarningText
 } from './style'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import FieldsDropdown from './FieldsDropdown'
import FiltersDropdown from './FiltersDropdown'
import down from '../../../../assets/stock/FilterMenu/angle-down.svg'
import { textHeaders, numberHeaders } from './utils'
import StockContext from '../../../../contexts/StockContext'
import SectionContext from '../../../../contexts/SectionContext'

interface TableFilter {
    field: string
    type: string
    text: string
  }

interface FiltersMenuProps {
    anchorRef: React.MutableRefObject<HTMLDivElement | null>
}

const FiltersMenu = React.forwardRef<HTMLDivElement, FiltersMenuProps>(({ anchorRef }, forwardRef) => {
    const { stockFilters, setStockFilters, movementFilters, setMovementFilters } = useContext(TableFiltersContext)
    const { stockSection } = useContext(SectionContext)
    const [ fieldsDropdown, setFieldsDropdown ] = useState<boolean>(false)
    const [ filtersDropdown, setFiltersDropdown ] = useState<boolean>(false)
    const [ numberWarning, setNumberWarning ] = useState<boolean>(false)
    const [ fields, setFields ] = useState<string>('')
    const [ filters, setFilters ] = useState<string>('')
    const [ text, setText ] = useState<string>('')

    const fieldsRef = useRef<HTMLDivElement>(null)
    const filtersRef = useRef<HTMLDivElement>(null)
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)

    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current

        if (!anchor) {
            setMenuPosition(null)
            return
        }

        const rect = anchor.getBoundingClientRect()
        setMenuPosition({
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2,
        })
    }, [anchorRef])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        if (name === 'field'){
            setFields(value)
        } else if (name === 'filter'){
            setFilters(value)
        } else if (name === 'text'){
            if (numberHeaders.includes(fields) && isNaN(Number(value))){
                setNumberWarning(true)
                return
            }
            setText(value)
        }
        setNumberWarning(false)
        return
    }

    const handleNewField = (value: string) => {
        setFields(value)
        setFieldsDropdown(false)
    }

    const handleNewFilter = (value: string) => {
        setFilters(value)
        setFiltersDropdown(false)
    }

    const submitFilter = () => {
        if (fields === "" || filters === "" || text == ""){
            alert("Algum campo está vazio!")
            return
        }
        if (stockSection === 'stock'){
            const newFilter: TableFilter = {field: fields, type: filters, text: text}
            const newFilters = [...stockFilters, newFilter]
            setStockFilters(newFilters)
        } else {
            const newFilter: TableFilter = {field: fields, type: filters, text: text}
            const newFilters = [...movementFilters, newFilter]
            setMovementFilters(newFilters)
        }
    }

    const FilterInput :React.FC = () => {
        if (fields === ''){
            return (
                <FilterSelectDiv>
                    <FilterSelectInput
                        placeholder='Preencha acima'
                        disabled
                    />
                </FilterSelectDiv>
            )
        }
        return (
            <FilterSelectDiv>
                <FilterSelectInput
                    placeholder='Tipo de filtro'
                    name='filter'
                    value={filters}
                    onChange={handleChange}
                    onClick={() => setFiltersDropdown(true)}
                    autoComplete="off"
                />
                <FilterSelectImg src={down} alt='Down arrow' onClick={() => setFiltersDropdown(!filtersDropdown)}/>
                {filtersDropdown ? <FiltersDropdown ref={filtersRef} isNumber={numberHeaders.includes(fields)} text={filters} onClick={handleNewFilter}/> : null}
            </FilterSelectDiv>
        )
    }

    const Warning: React.FC = () => {
        return(
            <WarningDiv>
                <WarningText>Campo numérico precisa de um número!</WarningText>
            </WarningDiv>
        )
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (fieldsRef.current && !fieldsRef.current.contains(event.target as Node)) {
                setFieldsDropdown(false)
            }
            if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
                setFiltersDropdown(false)
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);}
    }, []);

    useEffect(() => {
        updatePosition()
    }, [updatePosition])

    useEffect(() => {
        window.addEventListener('resize', updatePosition)
        window.addEventListener('scroll', updatePosition, true)

        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [updatePosition])

    if (!menuPosition) {
        return null
    }

    const menuContent = (
        <Container ref={forwardRef} style={{ top: menuPosition.top, left: menuPosition.left }}>
            <FilterMainText>Filtrar</FilterMainText>
            <FilterSelectDiv>
                <FilterSelectInput
                    placeholder='Filtrar por'
                    name='field'
                    value={fields}
                    onChange={handleChange}
                    onClick={() => setFieldsDropdown(true)}
                    autoComplete="off"
                />
                <FilterSelectImg src={down} alt='Down arrow' onClick={() => setFieldsDropdown(!fieldsDropdown)}/>
                {fieldsDropdown ? <FieldsDropdown ref={fieldsRef} text={fields} onClick={handleNewField}/> : null}
            </FilterSelectDiv>
            <FilterInput/>
            <FilterTextInput 
                placeholder='Digite aqui'
                name='text'
                value={text}
                onChange={handleChange}
                autoComplete="off"
                className={numberWarning ? 'warning' : ''}
            />
            {numberWarning? <Warning/> : null}
            <ApplyFilterButton onClick={submitFilter}>Aplicar filtro</ApplyFilterButton>
        </Container>
    )

    return createPortal(menuContent, document.body)
})

FiltersMenu.displayName = "FiltersMenu";

export default FiltersMenu